import { N, round, ceil } from './helpers.mjs';

export const DRAINAGE = [
  // ============================================================
  // DRAINAGE — groundworks drainage suite
  // ============================================================
  {
    slug: 'drainage-patio-fall',
    trade: 'drainage',
    name: 'Drainage: Patio / Surface Fall',
    blurb: 'Calculate the correct fall for porcelain, sandstone, concrete or tarmac surfaces. Includes mm per metre and total drop across the run.',
    inputs: [
      { key: 'distance', label: 'Distance / patio length', unit: 'm', placeholder: '4' },
      {
        key: 'surface',
        label: 'Surface type',
        type: 'select',
        default: 'porcelain',
        options: [
          { value: 'porcelain',  label: 'Porcelain / natural sandstone — 1:60 (16.7mm/m)' },
          { value: 'concrete',   label: 'Concrete patio / flags — 1:80 (12.5mm/m)' },
          { value: 'permeable',  label: 'Permeable block paving — 1:100 (10mm/m)' },
          { value: 'tarmac',     label: 'Tarmac / driveway — 1:40 (25mm/m)' },
          { value: 'gravel',     label: 'Gravel / decorative — 1:50 (20mm/m)' },
          { value: 'custom',     label: 'Custom ratio (enter below)' },
        ],
      },
      { key: 'customRatio', label: 'Custom fall ratio – 1 in __ (Custom only)', unit: '', placeholder: '60', default: 60 },
    ],
    compute: (v) => {
      const RATIO = { porcelain: 60, concrete: 80, permeable: 100, tarmac: 40, gravel: 50 };
      const ratio = v.surface === 'custom'
        ? Math.max(1, N(v.customRatio) || 60)
        : (RATIO[v.surface || 'porcelain'] || 60);
      const distance = N(v.distance);
      const mmPerM = 1000 / ratio;
      const totalMmFall = distance * mmPerM;
      const numCourses = Math.ceil(totalMmFall / 65); // 65mm coursing brick height
      return [
        { label: 'Fall ratio', value: `1 : ${ratio}`, unit: '' },
        { label: 'Fall per metre', value: round(mmPerM, 1), unit: 'mm/m' },
        { label: `Total drop over ${round(distance, 2)}m`, value: round(totalMmFall, 1), unit: 'mm' },
        { label: 'Total drop', value: round(totalMmFall / 10, 1), unit: 'cm' },
        { label: 'Equivalent in coursing bricks (65mm)', value: numCourses, unit: 'courses' },
      ];
    },
  },
  {
    slug: 'drainage-pipe-gradient',
    trade: 'drainage',
    name: 'Drainage: Pipe Gradient & Drop',
    blurb: 'Foul or surface water pipe runs. Calculates total drop and invert level over the run for any UK gradient.',
    inputs: [
      { key: 'runLength', label: 'Pipe run length', unit: 'm', placeholder: '10' },
      {
        key: 'pipeType',
        label: 'Pipe type / size',
        type: 'select',
        default: 'foul-110',
        options: [
          { value: 'foul-110',     label: 'Foul 110mm — 1:40 (Part H min) (25mm/m)' },
          { value: 'foul-110-max', label: 'Foul 110mm — 1:80 (max recommended) (12.5mm/m)' },
          { value: 'surface-110',  label: 'Surface 110mm — 1:100 (10mm/m)' },
          { value: 'pipe-150',     label: 'Pipe 150mm — 1:150 (6.7mm/m)' },
          { value: 'pipe-225',     label: 'Pipe 225mm — 1:225 (4.4mm/m)' },
          { value: 'rainwater-68', label: 'Rainwater 68mm — 1:100 (10mm/m)' },
          { value: 'custom',       label: 'Custom gradient (enter below)' },
        ],
      },
      { key: 'customGradient', label: 'Custom gradient – 1 in __ (Custom only)', unit: '', placeholder: '80', default: 80 },
      { key: 'startInvert', label: 'Start invert depth (optional)', unit: 'mm', placeholder: '0', default: 0 },
    ],
    compute: (v) => {
      const RATIO = {
        'foul-110': 40, 'foul-110-max': 80, 'surface-110': 100,
        'pipe-150': 150, 'pipe-225': 225, 'rainwater-68': 100,
      };
      const ratio = v.pipeType === 'custom'
        ? Math.max(1, N(v.customGradient) || 80)
        : (RATIO[v.pipeType || 'foul-110'] || 40);
      const run = N(v.runLength);
      const mmPerM = 1000 / ratio;
      const totalDropMm = run * mmPerM;
      const startInvert = N(v.startInvert) || 0;
      const endInvert = startInvert + totalDropMm;
      return [
        { label: 'Gradient', value: `1 : ${ratio}`, unit: '' },
        { label: 'Drop per metre', value: round(mmPerM, 1), unit: 'mm/m' },
        { label: `Total drop over ${round(run, 2)}m`, value: round(totalDropMm, 1), unit: 'mm' },
        { label: 'Total drop', value: round(totalDropMm / 1000, 3), unit: 'm' },
        { label: 'End invert (relative to start)', value: round(endInvert, 1), unit: 'mm below datum' },
      ];
    },
  },
  {
    slug: 'drainage-soakaway',
    trade: 'drainage',
    name: 'Drainage: Soakaway Volume (BRE 365)',
    blurb: 'BRE Digest 365 simplified soakaway sizing for roof / driveway runoff. Sizes the void volume and equivalent crate count.',
    inputs: [
      { key: 'impArea', label: 'Impermeable area (roof / driveway)', unit: 'm²', placeholder: '80' },
      { key: 'rainfall', label: 'Design rainfall intensity', unit: 'mm/hr', placeholder: '50', default: 50 },
      { key: 'duration', label: 'Storm duration', unit: 'hours', placeholder: '0.25', default: 0.25 },
      {
        key: 'soil',
        label: 'Soil percolation',
        type: 'select',
        default: 'medium',
        options: [
          { value: 'gravel',   label: 'Gravel / sandy — very free draining (k≈10⁻³ m/s)' },
          { value: 'sand',     label: 'Sandy soil — good (k≈10⁻⁴ m/s)' },
          { value: 'medium',   label: 'Loam / mixed — average (k≈10⁻⁵ m/s)' },
          { value: 'clay',     label: 'Heavy clay — poor (k≈10⁻⁶ m/s) – soakaway may be unsuitable' },
        ],
      },
      {
        key: 'fill',
        label: 'Soakaway fill',
        type: 'select',
        default: 'crate',
        options: [
          { value: 'crate',   label: 'Plastic crates / cellular (void ratio 95%)' },
          { value: 'gravel',  label: '20mm shingle backfill (void ratio 30%)' },
        ],
      },
    ],
    compute: (v) => {
      const FACTOR = { gravel: 0.6, sand: 0.8, medium: 1.0, clay: 1.5 }; // safety factor on volume
      const VOID = { crate: 0.95, gravel: 0.30 };
      const area = N(v.impArea);
      const intensity = N(v.rainfall) || 50;
      const duration = N(v.duration) || 0.25;
      const safety = FACTOR[v.soil || 'medium'] || 1.0;
      const voidRatio = VOID[v.fill || 'crate'] || 0.95;
      // Inflow volume = area × intensity (m/hr) × duration (hr)
      const inflow = area * (intensity / 1000) * duration;
      const requiredVoid = inflow * safety;
      const builtVolume = requiredVoid / voidRatio;
      // Crate equivalent (std 1m × 0.5m × 0.4m = 0.2 m³ crate)
      const crateCount = ceil(builtVolume / 0.2);
      const out = [
        { label: 'Runoff volume', value: round(inflow, 2), unit: 'm³' },
        { label: 'Required void volume (× safety)', value: round(requiredVoid, 2), unit: 'm³' },
        { label: 'Soakaway build volume', value: round(builtVolume, 2), unit: 'm³' },
        { label: 'Suggested pit (1.5m deep)', value: `${round(Math.sqrt(builtVolume / 1.5), 2)} × ${round(Math.sqrt(builtVolume / 1.5), 2)}m`, unit: '' },
      ];
      if (v.fill === 'crate') {
        out.push({ label: 'Std crates (1×0.5×0.4m = 0.2m³)', value: crateCount, unit: 'crates' });
      } else {
        out.push({ label: '20mm shingle backfill', value: round(builtVolume * 1.5, 2), unit: 'tonnes' });
      }
      if (v.soil === 'clay') {
        out.push({ label: '⚠️ Heavy clay', value: 'Soakaway likely unsuitable — consider alternative outfall.', unit: '' });
      }
      return out;
    },
  },
  {
    slug: 'drainage-french-drain',
    trade: 'drainage',
    name: 'Drainage: French Drain Trench Gravel',
    blurb: 'Trench gravel, perforated pipe and geotextile membrane for a French / land drain.',
    inputs: [
      { key: 'length', label: 'Trench length', unit: 'm', placeholder: '15' },
      { key: 'width', label: 'Trench width', unit: 'mm', placeholder: '300', default: 300 },
      { key: 'depth', label: 'Trench depth', unit: 'mm', placeholder: '600', default: 600 },
      { key: 'pipeDiameter', label: 'Perforated pipe diameter', unit: 'mm', placeholder: '100', default: 100 },
      { key: 'gravelFillPct', label: 'Gravel fill (% of trench)', unit: '%', placeholder: '90', default: 90 },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '5', default: 5 },
    ],
    compute: (v) => {
      const length = N(v.length);
      const widthM = N(v.width) / 1000;
      const depthM = N(v.depth) / 1000;
      const trenchVol = length * widthM * depthM;
      const fillFrac = (N(v.gravelFillPct) || 90) / 100;
      const wastage = (N(v.wastage) || 0) / 100;
      // 20mm shingle density ~1.5 t/m³
      const gravelVol = trenchVol * fillFrac * (1 + wastage);
      const gravelTonnes = gravelVol * 1.5;
      // Geotextile: trench perimeter (2 sides + base + 200mm laps) × length
      const geotextileArea = length * ((widthM + 2 * depthM) + 0.4);
      return [
        { label: 'Trench volume', value: round(trenchVol, 2), unit: 'm³' },
        { label: 'Perforated pipe needed', value: round(length, 2), unit: 'm' },
        { label: '20mm shingle (inc. wastage)', value: round(gravelTonnes, 2), unit: 'tonnes' },
        { label: '20mm shingle (volume)', value: round(gravelVol, 2), unit: 'm³' },
        { label: 'Bulk bags (0.85t each)', value: ceil(gravelTonnes / 0.85), unit: 'bags' },
        { label: 'Geotextile membrane', value: round(geotextileArea, 2), unit: 'm²' },
      ];
    },
  },
  {
    slug: 'drainage-rainwater-discharge',
    trade: 'drainage',
    name: 'Drainage: Rainwater Discharge & Pipe Size',
    blurb: 'Sizes downpipes / gulleys for roof run-off per BS EN 12056-3. Tells you the litres-per-second and recommended pipe diameter.',
    inputs: [
      { key: 'roofArea', label: 'Effective roof area (plan)', unit: 'm²', placeholder: '50' },
      { key: 'rainfall', label: 'Design rainfall intensity', unit: 'mm/hr', placeholder: '75', default: 75 },
      { key: 'pitchFactor', label: 'Pitch factor (1.0 flat, 1.15 30°, 1.29 45°)', unit: '', placeholder: '1', default: 1 },
    ],
    compute: (v) => {
      const area = N(v.roofArea);
      const intensity = N(v.rainfall) || 75;
      const pitch = N(v.pitchFactor) || 1;
      const effArea = area * pitch;
      // Q (L/s) = area × intensity / 3600
      const qLitresSec = (effArea * intensity) / 3600;
      // Pipe sizing per BS EN 12056-3 (round half-full capacity at 1:100)
      let pipeRec = '50mm downpipe';
      if (qLitresSec > 0.7 && qLitresSec <= 1.5) pipeRec = '65mm downpipe';
      else if (qLitresSec > 1.5 && qLitresSec <= 2.6) pipeRec = '75mm downpipe';
      else if (qLitresSec > 2.6 && qLitresSec <= 5.4) pipeRec = '100mm downpipe';
      else if (qLitresSec > 5.4 && qLitresSec <= 12) pipeRec = '110mm underground pipe';
      else if (qLitresSec > 12) pipeRec = '160mm+ underground pipe — split downpipes';
      return [
        { label: 'Effective catchment (with pitch)', value: round(effArea, 2), unit: 'm²' },
        { label: 'Peak discharge', value: round(qLitresSec, 2), unit: 'L/s' },
        { label: 'Peak discharge', value: round(qLitresSec * 60, 1), unit: 'L/min' },
        { label: 'Recommended pipe', value: pipeRec, unit: '' },
      ];
    },
  },

  {
    slug: 'block-paving',
    trade: 'groundworkers',
    name: 'Block Paving Area',
    blurb: 'Single-size, Trio (mixed-size) packs or custom block dimensions — all common UK block paving formats supported.',
    inputs: [
      { key: 'length', label: 'Length', unit: 'm', placeholder: '6' },
      { key: 'width', label: 'Width', unit: 'm', placeholder: '4' },
      {
        key: 'blockType',
        label: 'Block / paver type',
        type: 'select',
        default: 'rect-200x100',
        options: [
          // Single-size rectangular
          { value: 'rect-200x100',     label: 'Standard 200×100mm (50/m²)' },
          { value: 'square-100x100',   label: 'Square 100×100mm (100/m²)' },
          { value: 'large-200x200',    label: 'Large 200×200mm (25/m²)' },
          { value: 'tegula-240x160',   label: 'Tegula 240×160mm (26/m²)' },
          { value: 'olde-200x134',     label: 'Olde London 200×134mm (37/m²)' },
          { value: 'cobble-100x100',   label: 'Cobble setts 100×100mm (100/m²)' },
          { value: 'cobble-200x100',   label: 'Cobble setts 200×100mm (50/m²)' },
          { value: 'cobble-100x65',    label: 'Granite setts 100×65mm (154/m²)' },
          // Trio (mixed-size) packs
          { value: 'trio-tegula',      label: 'Tegula Trio – mixed 240/160/80mm (38/m²)' },
          { value: 'trio-country',     label: 'Country Cobbles Trio – mixed (50/m²)' },
          { value: 'trio-olde-london', label: 'Olde London Trio – mixed (50/m²)' },
          { value: 'trio-drivesys',    label: 'Drivesys Original Trio – mixed (32/m²)' },
          // Custom dimensions
          { value: 'custom',           label: 'Custom block size (enter mm below)' },
        ],
      },
      { key: 'customL', label: 'Custom block length (Custom only)', unit: 'mm', placeholder: '200', default: 200 },
      { key: 'customW', label: 'Custom block width (Custom only)', unit: 'mm', placeholder: '100', default: 100 },
      { key: 'packOverride', label: 'Pack size (m² per pack — leave 0 to use preset)', unit: 'm²', placeholder: '0', default: 0 },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '5', default: 5 },
      {
        key: 'screedMaterial',
        label: 'Laying course (screed) material',
        type: 'select',
        default: 'sharp-sand',
        options: [
          { value: 'sharp-sand', label: 'Sharp sand (1.6 t/m³)' },
          { value: '6mm-grit', label: '6mm grit / grit sand (1.7 t/m³)' },
        ],
      },
      { key: 'screedDepth', label: 'Screed thickness', unit: 'mm', placeholder: '50', default: 50 },
    ],
    compute: (v) => {
      // Blocks per m² for each known type
      const PER_M2 = {
        'rect-200x100': 50,
        'square-100x100': 100,
        'large-200x200': 25,
        'tegula-240x160': 26,
        'olde-200x134': 37,
        'cobble-100x100': 100,
        'cobble-200x100': 50,
        'cobble-100x65': 154,
        'trio-tegula': 38,
        'trio-country': 50,
        'trio-olde-london': 50,
        'trio-drivesys': 32,
      };
      // Typical UK supplier pack size (m² per pack) per block type
      const PACK_M2 = {
        'rect-200x100': 9.78,
        'square-100x100': 4.89,
        'large-200x200': 6.00,
        'tegula-240x160': 5.10,
        'olde-200x134': 5.04,
        'cobble-100x100': 4.00,
        'cobble-200x100': 8.07,
        'cobble-100x65': 5.40,
        'trio-tegula': 13.65,
        'trio-country': 10.50,
        'trio-olde-london': 9.00,
        'trio-drivesys': 5.94,
      };
      const blockType = v.blockType || 'rect-200x100';
      let perM2 = PER_M2[blockType] || 50;
      let blockLabel = 'Pavers';
      if (blockType === 'custom') {
        const cL = N(v.customL); const cW = N(v.customW);
        if (cL > 0 && cW > 0) perM2 = 1_000_000 / (cL * cW);
        blockLabel = `Pavers (${Math.round(N(v.customL) || 0)}×${Math.round(N(v.customW) || 0)}mm)`;
      }
      const area = N(v.length) * N(v.width);
      const areaIncWaste = area * (1 + N(v.wastage) / 100);
      const blocks = ceil(areaIncWaste * perM2);
      // Pack size: user override wins, otherwise use preset for the chosen type
      const packOverride = N(v.packOverride);
      const packSize = packOverride > 0 ? packOverride : (PACK_M2[blockType] || 0);
      const out = [
        { label: 'Area', value: round(area, 2), unit: 'm²' },
        { label: 'Blocks per m²', value: round(perM2, 1), unit: 'blocks/m²' },
        { label: blockLabel, value: blocks, unit: 'blocks' },
        { label: 'Kiln-dried jointing sand', value: round(area * 4, 1), unit: 'kg' },
      ];
      if (packSize > 0) {
        out.splice(3, 0,
          { label: `Pack size used (${packOverride > 0 ? 'your override' : 'typical supplier'})`, value: round(packSize, 2), unit: 'm²/pack' },
          { label: 'Packs needed (inc. wastage)', value: ceil(areaIncWaste / packSize), unit: 'packs' },
        );
      }
      // Laying course (screed) — sharp sand or 6mm grit at the chosen thickness (default 50mm)
      const screedMat = v.screedMaterial || 'sharp-sand';
      const screedDepth = N(v.screedDepth) || 50; // mm
      const DENS = { 'sharp-sand': 1.6, '6mm-grit': 1.7 }; // tonnes per m³
      const dens = DENS[screedMat] || 1.6;
      const matLabel = screedMat === '6mm-grit' ? '6mm grit' : 'Sharp sand';
      const screedVol = areaIncWaste * (screedDepth / 1000); // m³ (inc. wastage)
      const screedTonnes = screedVol * dens;
      out.push(
        { label: `Screed — ${matLabel} @ ${round(screedDepth, 0)}mm`, value: round(screedVol, 2), unit: 'm³' },
        { label: `Screed weight (${matLabel})`, value: round(screedTonnes, 2), unit: 'tonnes' },
        { label: 'Screed as bulk bags (~800kg)', value: ceil(screedTonnes / 0.8), unit: 'bags' },
      );
      return out;
    },
  },
  {
    slug: 'paving-slabs',
    trade: 'groundworkers',
    name: 'Paving Slabs Required',
    blurb: 'Work out how many single-size slabs you need from an area and slab dimensions (mm). Includes optional 10% wastage for cuts.',
    inputs: [
      { key: 'area', label: 'Area to cover', unit: 'm²', placeholder: '20' },
      { key: 'slabW', label: 'Slab width', unit: 'mm', placeholder: '600', default: 600 },
      { key: 'slabL', label: 'Slab length', unit: 'mm', placeholder: '600', default: 600 },
      {
        key: 'wastage',
        label: 'Add wastage?',
        type: 'select',
        default: '10',
        options: [
          { value: '0', label: 'No wastage' },
          { value: '5', label: '5% (straight runs)' },
          { value: '10', label: '10% (recommended)' },
          { value: '15', label: '15% (lots of cuts / circular)' },
        ],
      },
    ],
    compute: (v) => {
      const area = N(v.area);
      const slabArea = (N(v.slabW) / 1000) * (N(v.slabL) / 1000);
      if (slabArea <= 0 || area <= 0) {
        return [
          { label: 'Slabs per m²', value: 0, unit: '' },
          { label: 'Slabs needed (no wastage)', value: 0, unit: 'slabs' },
          { label: 'Slabs needed (incl. wastage)', value: 0, unit: 'slabs' },
        ];
      }
      const perM2 = 1 / slabArea;
      const baseSlabs = ceil(area * perM2);
      const waste = N(v.wastage);
      const withWaste = ceil(area * perM2 * (1 + waste / 100));
      return [
        { label: 'Slab size', value: `${N(v.slabW)}×${N(v.slabL)}`, unit: 'mm' },
        { label: 'Slabs per m²', value: round(perM2, 2), unit: '/m²' },
        { label: 'Slabs (no wastage)', value: baseSlabs, unit: 'slabs' },
        { label: `Slabs (incl. ${waste}%)`, value: withWaste, unit: 'slabs' },
        { label: 'Total coverage', value: round(withWaste * slabArea, 2), unit: 'm²' },
      ];
    },
  },
  {
    slug: 'bedding-mortar',
    trade: 'groundworkers',
    name: 'Sand & Cement (Bedding Mortar)',
    blurb: 'Mortar bed for laying patios & paving (per m²) or setts, kerbs & edgings (per lineal metre). Works out mortar volume, cement bags and sharp sand — including bed thickness and optional haunching.',
    inputs: [
      {
        key: 'mode',
        label: 'What are you laying?',
        type: 'select',
        default: 'area',
        options: [
          { value: 'area',   label: 'Patio / paving slabs — full mortar bed (per m²)' },
          { value: 'setts',  label: 'Setts / cobbles — full mortar bed (per m²)' },
          { value: 'linear', label: 'Kerbs / edgings / setts course — per lineal metre' },
        ],
      },
      { key: 'length', label: 'Area length / run length', unit: 'm', placeholder: '10' },
      { key: 'width', label: 'Area width (per-m² modes only)', unit: 'm', placeholder: '4', default: 0 },
      { key: 'bedThickness', label: 'Mortar bed thickness', unit: 'mm', placeholder: '40', default: 40 },
      { key: 'bedWidth', label: 'Bed width (lineal mode only)', unit: 'mm', placeholder: '150', default: 150 },
      {
        key: 'haunch',
        label: 'Haunching (lineal mode only)',
        type: 'select',
        default: 'none',
        options: [
          { value: 'none', label: 'No haunch' },
          { value: 'one',  label: 'One side — 100×100mm haunch' },
          { value: 'both', label: 'Both sides — 100×100mm haunch' },
        ],
      },
      {
        key: 'mix',
        label: 'Mix ratio (cement : sharp sand)',
        type: 'select',
        default: '1:4',
        options: [
          { value: '1:3', label: '1:3 — strong (setts, kerbs, heavy traffic)' },
          { value: '1:4', label: '1:4 — standard paving / patio bed (recommended)' },
          { value: '1:5', label: '1:5 — general patio bed' },
          { value: '1:6', label: '1:6 — lean / light foot traffic' },
        ],
      },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '10', default: 10 },
    ],
    compute: (v) => {
      const mode = v.mode || 'area';
      const t = N(v.bedThickness) / 1000; // bed thickness in m
      const wastage = N(v.wastage) || 0;
      let wetVol = 0; // m³
      let basisLabel = '';
      if (mode === 'linear') {
        const run = N(v.length);
        const bw = N(v.bedWidth) / 1000;
        const bed = run * bw * t;
        // haunch: right-triangle 100×100mm cross-section = 0.5×0.1×0.1 = 0.005 m² per side
        const sides = v.haunch === 'both' ? 2 : v.haunch === 'one' ? 1 : 0;
        const haunchVol = 0.5 * 0.1 * 0.1 * sides * run;
        wetVol = bed + haunchVol;
        basisLabel = `${round(run, 2)} m run × ${N(v.bedWidth)}mm bed${sides ? ` + ${sides === 2 ? '2 haunches' : '1 haunch'}` : ''}`;
      } else {
        const area = N(v.length) * N(v.width);
        wetVol = area * t;
        basisLabel = `${round(area, 2)} m² × ${N(v.bedThickness)}mm bed`;
      }
      if (wetVol <= 0) {
        return [{ label: 'Enter dimensions to calculate', value: 0, unit: '' }];
      }
      wetVol = wetVol * (1 + wastage / 100);
      const X = Math.max(1, parseFloat((v.mix || '1:4').split(':')[1]) || 4);
      const dry = wetVol * 1.25; // wet mortar → dry material volume
      const cementVol = dry / (1 + X);
      const sandVol = dry * X / (1 + X);
      const cementKg = cementVol * 1440; // cement ~1440 kg/m³
      const sandKg = sandVol * 1600;     // sharp sand ~1600 kg/m³
      return [
        { label: 'Basis', value: basisLabel, unit: '' },
        { label: 'Mortar bed thickness', value: N(v.bedThickness), unit: 'mm' },
        { label: 'Mortar volume (incl. wastage)', value: round(wetVol, 3), unit: 'm³' },
        { label: 'Mix used', value: `1 : ${X} cement : sharp sand`, unit: '' },
        { label: 'Cement (25kg bags)', value: ceil(cementKg / 25), unit: 'bags' },
        { label: 'Sharp sand', value: round(sandKg / 1000, 2), unit: 'tonnes' },
        { label: 'Sand bulk bags (0.85t each)', value: ceil((sandKg / 1000) / 0.85), unit: 'bags' },
      ];
    },
  },
  {
    slug: 'mixed-paving-pack',
    trade: 'groundworkers',
    name: 'Mixed-Size Patio Pack (Indian Sandstone)',
    blurb: 'Work out how many multi-size patio packs (e.g. Indian sandstone, Marshalls, Bradstone) you need for a given area, with a breakdown of slabs by size per pack.',
    inputs: [
      { key: 'area', label: 'Area to cover', unit: 'm²', placeholder: '25' },
      {
        key: 'pack',
        label: 'Pack type',
        type: 'select',
        default: 'project-19.35',
        options: [
          { value: 'project-19.35', label: 'Project Pack — 19.35m² (4 sizes)' },
          { value: 'mini-15.30', label: 'Mini Pack — 15.30m² (4 sizes)' },
          { value: 'garden-10.20', label: 'Garden Pack — 10.20m² (3 sizes)' },
          { value: 'marshalls-18.90', label: 'Marshalls Fairstone — 18.90m² (4 sizes)' },
          { value: 'custom', label: 'Custom — set coverage below' },
        ],
      },
      { key: 'customCoverage', label: 'Custom pack coverage (if Custom)', unit: 'm²', placeholder: '19.35', default: 19.35 },
      {
        key: 'wastage',
        label: 'Add wastage?',
        type: 'select',
        default: '10',
        options: [
          { value: '0', label: 'No wastage' },
          { value: '5', label: '5% (straight runs)' },
          { value: '10', label: '10% (recommended)' },
          { value: '15', label: '15% (lots of cuts / circular)' },
        ],
      },
    ],
    compute: (v) => {
      const area = N(v.area);
      const waste = N(v.wastage);
      // Pack definitions: slabs per pack by size (mm), plus total m² coverage per pack.
      // Based on typical UK Indian sandstone calibrated patio packs.
      const PACKS = {
        'project-19.35': {
          coverage: 19.35,
          slabs: [
            { size: '900×600', count: 8 },
            { size: '600×600', count: 14 },
            { size: '600×290', count: 14 },
            { size: '290×290', count: 18 },
          ],
        },
        'mini-15.30': {
          coverage: 15.30,
          slabs: [
            { size: '900×600', count: 6 },
            { size: '600×600', count: 10 },
            { size: '600×290', count: 12 },
            { size: '290×290', count: 14 },
          ],
        },
        'garden-10.20': {
          coverage: 10.20,
          slabs: [
            { size: '600×600', count: 14 },
            { size: '600×290', count: 8 },
            { size: '290×290', count: 8 },
          ],
        },
        'marshalls-18.90': {
          coverage: 18.90,
          slabs: [
            { size: '845×560', count: 10 },
            { size: '560×560', count: 16 },
            { size: '560×275', count: 16 },
            { size: '275×275', count: 16 },
          ],
        },
      };
      const isCustom = v.pack === 'custom';
      const coverage = isCustom ? N(v.customCoverage) || 19.35 : (PACKS[v.pack]?.coverage || 19.35);
      const slabs = isCustom ? null : PACKS[v.pack]?.slabs;
      if (area <= 0 || coverage <= 0) {
        return [{ label: 'Packs required', value: 0, unit: 'packs' }];
      }
      const packsNeeded = ceil((area * (1 + waste / 100)) / coverage);
      const totalCoverage = packsNeeded * coverage;
      const excess = round(totalCoverage - area, 2);
      const out = [
        { label: 'Pack coverage', value: coverage, unit: 'm²/pack' },
        { label: `Packs required (incl. ${waste}%)`, value: packsNeeded, unit: 'packs' },
        { label: 'Total coverage bought', value: round(totalCoverage, 2), unit: 'm²' },
        { label: 'Excess over area', value: excess, unit: 'm²' },
      ];
      if (slabs && slabs.length) {
        slabs.forEach((s) => {
          out.push({
            label: `${s.size}mm slabs`,
            value: s.count * packsNeeded,
            unit: `(${s.count}/pack)`,
          });
        });
        const totalSlabs = slabs.reduce((sum, s) => sum + s.count * packsNeeded, 0);
        out.push({ label: 'Total slabs', value: totalSlabs, unit: 'slabs' });
      }
      return out;
    },
  },
  {
    slug: 'fence-panels',
    trade: 'groundworkers',
    name: 'Fence Panels & Posts',
    blurb: 'Standard 6ft (1.83m) panels. One post extra per run.',
    inputs: [
      { key: 'length', label: 'Total fence length', unit: 'm', placeholder: '20' },
      { key: 'panel', label: 'Panel width', unit: 'm', placeholder: '1.83', default: 1.83 },
    ],
    compute: (v) => {
      const panels = ceil(N(v.length) / (N(v.panel) || 1.83));
      return [
        { label: 'Panels', value: panels, unit: 'panels' },
        { label: 'Gravel boards', value: panels, unit: 'boards' },
        { label: 'Posts', value: panels + 1, unit: 'posts' },
        { label: 'Postmix (20kg)', value: (panels + 1) * 2, unit: 'bags' },
      ];
    },
  },
  {
    slug: 'decking-boards',
    trade: 'groundworkers',
    name: 'Decking Boards, Joists & Noggins',
    blurb: 'Std board 2.4m × 144mm with 5mm gap. Recommends the correct joist size and noggin rows for the deck span (TDCA / TRADA aligned).',
    inputs: [
      { key: 'length', label: 'Deck length', unit: 'm', placeholder: '4' },
      { key: 'width', label: 'Deck width (joist span)', unit: 'm', placeholder: '3' },
      { key: 'boardLen', label: 'Board length', unit: 'm', placeholder: '2.4', default: 2.4 },
      { key: 'boardW', label: 'Board width', unit: 'mm', placeholder: '144', default: 144 },
      { key: 'joistSpacing', label: 'Joist spacing (centres)', unit: 'mm', placeholder: '400', default: 400 },
      { key: 'postSpacing', label: 'Support post spacing (centres)', unit: 'm', placeholder: '1.5', default: 1.5 },
    ],
    compute: (v) => {
      const length = N(v.length);
      const width = N(v.width);
      const spacingMm = Math.max(100, N(v.joistSpacing) || 400);
      const rows = ceil(width / ((N(v.boardW) + 5) / 1000));
      const perRow = ceil(length / (N(v.boardLen) || 2.4));
      const total = rows * perRow;
      // Joists run across the width — count along the length, spaced at the chosen centres
      const joists = length > 0 ? (ceil(length / (spacingMm / 1000)) + 1) : 0;
      // Recommend joist size by span (TDCA / TRADA — at 400mm centres for C16/C24 graded timber)
      let joistSize = '—';
      if (width <= 0) joistSize = 'Enter deck width';
      else if (width <= 1.5) joistSize = '100 × 50mm (4×2")';
      else if (width <= 2.5) joistSize = '150 × 50mm (6×2")';
      else if (width <= 3.0) joistSize = '175 × 50mm (7×2")';
      else if (width <= 3.5) joistSize = '200 × 50mm (8×2")';
      else joistSize = '225 × 50mm (9×2") + intermediate bearer recommended';
      // Noggin rows: one row per 1.2m of span, mid-span
      const nogginRows = width > 1.2 ? Math.max(1, ceil(width / 1.2) - 1) : 0;
      const noggins = joists > 1 ? nogginRows * (joists - 1) : 0;
      // Each noggin piece fits between two joists: (centre-to-centre spacing) − joist thickness (~50mm)
      const nogginPieceLenM = Math.max(0, (spacingMm - 50) / 1000);
      const nogginLinearM = round(noggins * nogginPieceLenM, 2);
      // Support posts for the frame (grid) + Postcrete (2 × 20kg bags per post)
      const postSpacing = Math.max(0.5, N(v.postSpacing) || 1.5);
      const postsAlong = length > 0 ? ceil(length / postSpacing) + 1 : 0;
      const postsAcross = width > 0 ? ceil(width / postSpacing) + 1 : 0;
      const posts = postsAlong * postsAcross;
      const postcrete = posts * 2;
      return [
        { label: 'Rows', value: rows, unit: 'rows' },
        { label: 'Boards per row', value: perRow, unit: '' },
        { label: 'Total boards', value: total, unit: 'boards' },
        { label: `Joists @${spacingMm}mm centres`, value: joists, unit: 'joists' },
        { label: 'Recommended joist size', value: joistSize, unit: '' },
        { label: `Noggins (${nogginRows} ${nogginRows === 1 ? 'row' : 'rows'} mid-span)`, value: noggins, unit: 'pieces' },
        { label: 'Noggin linear metres', value: nogginLinearM, unit: 'm' },
        { label: `Support posts (@${postSpacing}m grid)`, value: posts, unit: 'posts' },
        { label: 'Postcrete (20kg bags)', value: postcrete, unit: 'bags' },
      ];
    },
  },

];
